import CustomTable from "./CustomTable";
import {getStandardFilter, renewsColumns} from "./columnsUtils";
import {RenewsNegotiation, States, TableFilter, Views} from "types";
import React, {useCallback, useEffect, useState} from "react";
import {ColumnsType} from "antd/lib/table";
import {useSelector} from "react-redux";
import {
    QueryNegotiationsParam,
    useLazyGetNegotiationsQuery,
    useRenewNegotiationMutation
} from "redux/api/negotiationsApiSlice";
import {selectOptions} from "redux/features/optionsSlice";
import {Button, Menu, message, Space} from "antd";
import {useNavigate} from "react-router-dom";
import DropdownButton from "antd/es/dropdown/dropdown-button";
import MenuItem from "antd/lib/menu/MenuItem";
import {selectUser} from "../../../redux/features/userSlice";
import {LobModel} from "../../../types/options";
import Tag from "antd/es/tag";
import {getContrastYIQ} from "../../../utils/formatters";
import {cloneDeep} from "lodash";
import RenewsButton from "./RenewsButton";

const RenewsTable = () => {
    const [columns, setColumns] = useState<ColumnsType<RenewsNegotiation>>(renewsColumns)

    const options = useSelector(selectOptions)
    const user = useSelector(selectUser)
    const [triggerGetNegotiations, {data: negotiations, isLoading, isFetching, error}] = useLazyGetNegotiationsQuery()
    const [renew, {data}] = useRenewNegotiationMutation();
    const navigate = useNavigate()

    const handleGetData = useCallback((params: QueryNegotiationsParam | void) => {
        const stateFilter = {key: 'state', range: [States.InRinnovo]}
        if (params) {
            triggerGetNegotiations({
                ...params,
                filters: params.filters ? [...params.filters, stateFilter] : [stateFilter]
            })
        } else {
            triggerGetNegotiations({filters: [stateFilter]})
        }
    }, [triggerGetNegotiations])

    const handleConfirmClick = useCallback(async (negotiationUUID: string, toRenew: boolean, toModify: boolean | null) => {
        try {
            const response: { new_negotiation_uuid: string | null } = await renew({
                uuid: negotiationUUID,
                data: {renew: toRenew, modify: toModify}
            }).unwrap()
            message.success(toRenew ? 'Trattativa rinnovata con successo' : 'Trattativa bloccata correttamente', 2)
            if (toRenew && toModify && response.new_negotiation_uuid)
                navigate(response.new_negotiation_uuid)
            else {
                let updatedFilters: TableFilter[] = []
                if (user.preferences.tables.renews.filters?.length) {
                    updatedFilters = cloneDeep(user.preferences.tables.renews.filters)

                    let contractorFilter = updatedFilters.find(el => el.key === 'contractor')
                    if (contractorFilter?.range.length) {
                        updatedFilters = [...updatedFilters.filter(el => el.key !== 'contractor'), {key: 'contractor', range: contractorFilter.range.map(el => (el as {label: string, value: string}).value)}]
                    }

                    let brokerFilter = updatedFilters.find(el => el.key === 'broker')
                    if (brokerFilter?.range.length) {
                        updatedFilters = [...updatedFilters.filter(el => el.key !== 'broker'), {key: 'broker', range: brokerFilter.range.map(el => (el as {label: string, value: string}).value)}]
                    }
                }
                handleGetData({filters: updatedFilters, sorters: user.preferences.tables.renews.sorters})
            }
        } catch (e) {
            message.error('Rinnovo trattativa fallito', 2)
        }
    }, [handleGetData, navigate, renew, user.preferences.tables.renews.filters, user.preferences.tables.renews.sorters])

    //setting table: setting filters and negotiation action column
    useEffect(() => {
        setColumns(prevState => prevState.map(el => {
            switch (el.key) {
                case 'cancellation_terms_days':
                    return {...el, ...getStandardFilter(options.cancellationDays)}
                case 'lob':
                    return {
                        ...el,
                        render: (lob: LobModel) => {
                            const foundLob = options.lobs.find(el => el.uuid === lob.uuid)
                            if (foundLob) {
                                return <Tag
                                    style={{
                                        borderRadius: '100px',
                                        width: '5rem',
                                        height: '1.25rem',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        padding: '0 0.5rem',
                                        color: getContrastYIQ(foundLob.color)
                                    }}
                                    color={foundLob.color}
                                >
                                    {foundLob.name}
                                </Tag>
                            }
                        },
                        ...getStandardFilter(options.lobs.map(el => ({text: el.name, value: el.uuid})))
                    }
                case 'underwriter':
                    return {
                        ...el, ...getStandardFilter(options.underwriters.map(el => ({
                            text: el.user_full_name,
                            value: el.uuid
                        })))
                    }
                case 'renewsActions':
                    return {
                        ...el, render: (_, record) => {
                            return <Space onClick={(event) => event.stopPropagation()} size="middle">
                                <RenewsButton
                                    confirmRenewal={(toRenew, toModify) => handleConfirmClick(record.uuid, toRenew, toModify)}
                                    renewWithoutChanges={record.is_auto_renewal === true || record.is_auto_renewal === null}
                                    negId={record.uuid}/>
                            </Space>
                        }
                    }
                default:
                    return el
            }
        }))
    }, [handleConfirmClick, options.cancellationDays, options.lobs, options.underwriters])


    return <CustomTable view={Views.renews} cols={columns} setCols={setColumns} data={negotiations}
                        getData={handleGetData} isLoading={isLoading || isFetching} error={error}/>
}

export default RenewsTable